<script setup lang="ts">
// The "(i)" legend next to the Future and K_t column headings on the FOF home (Paolo 2026-11-03 19:xx): a button that
// opens an explainer — the full-size SectionSpark drawing of one live row ("dynamic information from one of the
// charts"), a key to every mark on it, and plain-English text with that row's numbers. It must say that the futures %
// is measured from the valuation point (the blue line), that the K_t % is measured from the valuation point against
// the official NAV, and that the K_t revaluation against the official NAV is drawn as the step on the vertical blue
// bar. Opens as a centred dialog (desktop) or a bottom sheet (phone); Escape, the backdrop and ✕ close it.
import { computed, onUnmounted, ref } from 'vue'
import SectionSpark from '../SectionSpark.vue'
import { STRIKE_BLUE, CLOSE_GREY, onPct, strikeStepIndex } from '../sectionSpark'
import { UP_GOOD, DOWN_BAD } from '../sparkline'

export interface LegendExample {
  fundLabel: string
  symbol: string           // the future's symbol on the future chart, 'K_t' on the K_t chart
  strike: string           // "12:00 strike"
  pctText: string          // the row's headline, e.g. '+0.77%'
  values: number[]
  closeAt: number | null; decisionAt: number | null; markAt: number | null
  closeValue?: number | null; decisionValue?: number | null; markValue: number | null
  stepFrom: number | null; stepTo: number | null
  startUtc: string | null; endUtc: string | null
  unit: string; dp: number
  decisionColour?: string | null
}

const props = defineProps<{ kind: 'future' | 'kt'; example: LegendExample | null }>()

const open = ref(false)
function toggle(e: Event) { e.preventDefault(); e.stopPropagation(); open.value ? close() : show() }
function show() { open.value = true; document.addEventListener('keydown', onKey) }
function close() { open.value = false; document.removeEventListener('keydown', onKey) }
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close() }
onUnmounted(() => document.removeEventListener('keydown', onKey))

const title = computed(() => props.kind === 'future' ? 'How to read the Future chart' : 'How to read the K_t chart')
const e = computed(() => props.example)
const hasChart = computed(() => (e.value?.values.length ?? 0) >= 2)
const fmt = (v: number, dp: number) => v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })
const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`
const on = computed(() => e.value ? onPct(e.value.closeValue, e.value.decisionValue) : null)
const strikeReached = computed(() => e.value?.markAt != null)
// only a step ON the strike bar is today's landing — the API also reports yesterday's landing at the window start (2026-11-04)
const step = computed(() => e.value && e.value.stepFrom != null && e.value.stepTo != null && e.value.stepFrom > 0
  && strikeStepIndex(e.value.values, e.value.markAt, e.value.stepFrom, e.value.stepTo) >= 0
  ? { from: e.value.stepFrom, to: e.value.stepTo, pct: (e.value.stepTo / e.value.stepFrom - 1) * 100 } : null)

/** The paragraphs, with the example row's live numbers. */
const paragraphs = computed<string[]>(() => {
  const x = e.value
  if (!x) return ['No chart data has loaded yet; the explanation below describes what the chart will show.']
  const unit = x.unit
  if (props.kind === 'future') {
    const out = [
      `${x.fundLabel}: ${x.symbol} is ${x.pctText} since the ${x.strike} — the valuation point, drawn as the vertical blue line. `
      + `That is the number shown in the row: the future's move from the moment the fund was valued, measured from the value at the blue line.`,
    ]
    out.push(strikeReached.value
      ? `Before the blue line the path runs from the previous exchange close (the grey dashed line) through the 08:40 decision bar, `
        + (on.value != null ? `and ON ${signed(on.value)} is the overnight move between those two bars — the futures move the signal saw at the decision. ` : `and ON is the overnight move between those two bars — the futures move the signal saw at the decision. `)
        + `After the blue line the section shows what the future has done since the valuation.`
      : `Today's ${x.strike} has not been reached yet, so the % is measured from yesterday's strike, where the chart starts; the grey dashed line is the previous exchange close and the bar after it the 08:40 decision`
        + (on.value != null ? ` (ON ${signed(on.value)} between them).` : '.'))
    out.push('Each coloured section is judged on its own: green when it ended at or above where it started, red when it ended below.')
    return out
  }
  const nav = x.markValue != null ? `${fmt(x.markValue, x.dp)}${unit}` : 'the latest official NAV'
  const out = [
    `${x.fundLabel}: the synthetic NAV is ${x.pctText} against the latest official NAV, ${nav} (the dashed level in this zoomed chart), measured from the ${x.strike} — the valuation point, drawn as the vertical blue line. `
    + `The synthetic NAV estimates the fund's price between official valuations from the futures that drive it.`,
  ]
  out.push(step.value
    ? `Today's official NAV came in at ${fmt(step.value.to, x.dp)}${unit} against a synthetic ${fmt(step.value.from, x.dp)}${unit} at the strike, so the chart is revalued on the vertical blue bar: `
      + `the ${signed(step.value.pct)} step on that bar is the adjustment against the official NAV (green when the official came in below the synthetic, red when above). After the bar the path is the drift from the official NAV.`
    : `Today's official NAV has not landed yet, so there is no revaluation step; when it lands, the adjustment against the official NAV is drawn as a step on the vertical blue bar, and the % is then measured from that official level.`)
  out.push('Each coloured section is judged on its own: green when it ended at or above where it started, red when it ended below.')
  return out
})

const key = computed(() => {
  const items = [
    { swatch: 'bar', colour: STRIKE_BLUE, text: `Vertical blue line — the valuation point (the ${e.value?.strike ?? '12:00 strike'}), the reference every % on this chart is measured from.` },
    { swatch: 'bar dashed', colour: CLOSE_GREY, text: 'Grey dashed line — the previous exchange close (16:00 New York for the US-anchored funds, 15:30 Tokyo for the Japan funds).' },
    { swatch: 'bar', colour: UP_GOOD, text: props.kind === 'future'
      ? 'Thin bar in the overnight colour — the 08:40 decision; ON is the move from the close to this bar.'
      : 'Thin bar — the 08:40 decision, in the same colour as the decision bar on the futures chart (the overnight move the signal saw).' },
    { swatch: 'seg', colour: UP_GOOD, text: 'Green section — ended at or above where it started.' },
    { swatch: 'seg', colour: DOWN_BAD, text: 'Red section — ended below where it started.' },
    { swatch: 'level', colour: '#e0e0e0', text: props.kind === 'future' ? 'Dashed level (zoomed chart only) — the value at the valuation point.' : 'Dashed level (zoomed chart only) — the latest official NAV.' },
  ]
  if (props.kind === 'kt') items.push({ swatch: 'step', colour: UP_GOOD, text: 'Thick step on the blue bar — the revaluation against the official NAV when it lands (green when the official is below the synthetic, red when above).' })
  return items
})
</script>

<template>
  <!-- the glyph is CSS content so the column heading's text stays "Future" / "K_t" -->
  <button type="button" class="fcl-btn" :data-testid="`fof-legend-${kind}`" :aria-expanded="open" :title="title" :aria-label="title" @click="toggle"></button>
  <Teleport to="body">
    <div v-if="open" class="fcl-backdrop" data-testid="fof-legend-backdrop" @click="close"></div>
    <div v-if="open" class="fcl-panel" data-testid="fof-legend-panel" :data-kind="kind" role="dialog" :aria-label="title" @click.stop>
      <div class="hd">
        <span class="ttl">{{ title }}</span>
        <span v-if="e" class="sub">{{ e.fundLabel }} · {{ e.symbol }} — live data from the row</span>
        <button type="button" class="x" aria-label="Close" data-testid="fof-legend-close" @click="close">✕</button>
      </div>
      <div v-if="hasChart && e" class="chart">
        <SectionSpark :values="e.values" size="zoom" :close-at="e.closeAt" :decision-at="e.decisionAt" :mark-at="e.markAt"
                      :base-value="e.markValue" :base-label="kind === 'kt' ? 'official NAV' : 'strike'" :mark-label="e.strike"
                      :step-from="e.stepFrom" :step-to="e.stepTo" :start-utc="e.startUtc" :end-utc="e.endUtc" :unit="e.unit" :dp="e.dp" :decision-colour="e.decisionColour ?? null" />
      </div>
      <ul class="key" data-testid="fof-legend-key">
        <li v-for="(k, i) in key" :key="i"><span class="sw" :class="k.swatch" :style="{ borderColor: k.colour, background: k.swatch === 'seg' || k.swatch === 'step' ? k.colour : 'transparent' }"></span>{{ k.text }}</li>
      </ul>
      <div class="text" data-testid="fof-legend-text">
        <p v-for="(p, i) in paragraphs" :key="i">{{ p }}</p>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.fcl-btn { display: inline-flex; align-items: center; justify-content: center; width: 15px; height: 15px; margin-left: 5px; padding: 0;
  border-radius: 50%; border: 1px solid #6ea8df; background: transparent; color: #6ea8df; font: 700 10px/1 'JetBrains Mono', Consolas, monospace;
  cursor: pointer; vertical-align: middle; }
.fcl-btn::before { content: 'i'; }
.fcl-btn:hover, .fcl-btn[aria-expanded="true"] { background: #1f3a5a; }
.fcl-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45); z-index: 999; }
.fcl-panel { position: fixed; z-index: 1000; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 680px; max-width: calc(100vw - 16px);
  max-height: calc(100vh - 24px); overflow: auto; background: #1a1a1a; color: #e0e0e0; border: 1px solid #3a3a3a; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5); padding: 12px 14px; display: grid; gap: 10px; font-size: 13px; line-height: 1.45; }
.hd { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.hd .ttl { font-weight: 700; font-size: 14px; }
.hd .sub { font-family: 'JetBrains Mono', Consolas, monospace; font-size: 11px; color: #9e9e9e; }
.hd .x { margin-left: auto; background: none; border: 0; color: #9e9e9e; font-size: 16px; cursor: pointer; padding: 2px 6px; }
.chart :deep(svg.section-spark.zoom) { height: 230px; }
.key { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; font-size: 12px; color: #bdbdbd; }
.key li { display: flex; align-items: center; gap: 8px; }
.sw { flex: 0 0 auto; display: inline-block; width: 18px; height: 12px; border: 0; }
.sw.bar { width: 0; height: 14px; border-left: 2px solid; }
.sw.bar.dashed { border-left-style: dashed; }
.sw.seg { height: 3px; border-radius: 2px; }
.sw.step { width: 4px; height: 14px; border-radius: 2px; }
.sw.level { height: 0; border-top: 1px dashed; }
.text p { margin: 0 0 6px; }
@media (max-width: 639px) {
  .fcl-panel { left: 0; right: 0; top: auto; bottom: 0; transform: none; width: auto; max-width: none; border-radius: 12px 12px 0 0;
    max-height: 85vh; padding-bottom: max(14px, env(safe-area-inset-bottom, 0px)); }
}
</style>
